import React, { useState } from 'react';
import { Plus, Trash2, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { t } from '@/i18n/ro';
import {
  createTimeEntry,
  deleteTimeEntry,
  getTimeEntryActivities,
  parseISODuration,
  formatHoursForDisplay,
  type TimeEntryActivity,
} from '@/services/timeentries.service';
import {
  Button,
  Input,
  DateInput,
  Select,
  Modal,
  EmptyState,
} from '@/components/common';
import type { TimeEntry } from '@/types';

interface TimeEntriesListProps {
  timeEntries: TimeEntry[];
  workPackageId: number;
  projectId: number;
  onRefresh: () => void;
  canAddTime?: boolean;
}

export function TimeEntriesList({
  timeEntries,
  workPackageId,
  projectId,
  onRefresh,
  canAddTime = true,
}: TimeEntriesListProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [activities, setActivities] = useState<TimeEntryActivity[]>([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data pentru înregistrare nouă
  const [formData, setFormData] = useState({
    hours: '',
    spentOn: new Date().toISOString().split('T')[0],
    activityId: '',
    comment: '',
  });

  const handleOpenAddModal = async () => {
    setIsLoadingActivities(true);
    setError(null);

    try {
      const activitiesData = await getTimeEntryActivities();
      setActivities(activitiesData);
      setShowAddModal(true);
    } catch (err) {
      console.error('Failed to load activities:', err);
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoadingActivities(false);
    }
  };

  const handleSave = async () => {
    if (!formData.hours || !formData.activityId) {
      setError('Completați toate câmpurile obligatorii');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await createTimeEntry({
        workPackageId,
        projectId,
        hours: parseFloat(formData.hours),
        spentOn: formData.spentOn,
        activityId: Number(formData.activityId),
        comment: formData.comment || undefined,
      });

      setShowAddModal(false);
      setFormData({
        hours: '',
        spentOn: new Date().toISOString().split('T')[0],
        activityId: '',
        comment: '',
      });
      onRefresh();
    } catch (err) {
      console.error('Failed to save time entry:', err);
      setError(t('timeTracking.errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (timeEntryId: number) => {
    if (!confirm('Sigur doriți să ștergeți această înregistrare?')) return;

    try {
      await deleteTimeEntry(timeEntryId);
      onRefresh();
    } catch (err) {
      console.error('Failed to delete time entry:', err);
    }
  };

  const activityOptions = activities.map((a) => ({
    value: a.id,
    label: a.name,
  }));

  return (
    <div>
      {/* Add button - only show if canAddTime */}
      {canAddTime && (
        <div className="mb-4">
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={handleOpenAddModal}
            isLoading={isLoadingActivities}
          >
            {t('timeTracking.add')}
          </Button>
        </div>
      )}

      {timeEntries.length === 0 ? (
        <EmptyState
          icon={<Clock className="w-12 h-12" />}
          title={t('timeTracking.noEntries')}
          description={canAddTime ? "Apăsați butonul de mai sus pentru a înregistra timp." : "Nu există înregistrări de timp pentru această activitate."}
        />
      ) : (
        <div className="space-y-3">
          {timeEntries.map((entry) => {
            const hours = parseISODuration(entry.hours);
            const activityName = entry._links.activity?.title || '';
            const userName = entry._links.user?.title || '';

            return (
              <div
                key={entry.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <Clock className="w-5 h-5 text-primary-500" />
                      {formatHoursForDisplay(hours)}
                    </div>
                    <span className="text-sm text-gray-500">{activityName}</span>
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    {format(parseISO(entry.spentOn), 'dd MMMM yyyy', { locale: ro })}
                    {userName && ` • ${userName}`}
                  </div>
                  {entry.comment?.raw && (
                    <p className="mt-2 text-sm text-gray-600">
                      {entry.comment.raw}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(entry.id)}
                >
                  <Trash2 className="w-4 h-4 text-gray-400 hover:text-danger-500" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal pentru adăugare */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t('timeTracking.add')}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowAddModal(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isSaving}
            >
              {t('common.save')}
            </Button>
          </div>
        }
      >
        {error && (
          <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <Input
            label={t('timeTracking.fields.hours')}
            type="number"
            min={0.25}
            step={0.25}
            value={formData.hours}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, hours: e.target.value }))
            }
            placeholder="ex: 2.5"
            required
          />

          <DateInput
            label={t('timeTracking.fields.spentOn')}
            value={formData.spentOn}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, spentOn: e.target.value }))
            }
            required
          />

          <Select
            label={t('timeTracking.fields.activity')}
            options={activityOptions}
            value={formData.activityId}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, activityId: e.target.value }))
            }
            placeholder={t('common.selectOption')}
            required
          />

          <Input
            label={t('timeTracking.fields.comment')}
            value={formData.comment}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, comment: e.target.value }))
            }
            placeholder="Comentariu opțional..."
          />
        </div>
      </Modal>
    </div>
  );
}
